import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  DocumentNode,
  MutationHookOptions,
  TypedDocumentNode
} from '@apollo/client';
import { useMutation } from '@apollo/client';
import { setButtonLoading, setError, setLoading } from '@common/slices';
import { extractMessage } from '@utils/helper-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';

export function useApiMutation<TData = any, TVariables = Record<string, any>>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  key: string,
  config?: {
    successMsg?: string;
    withLoader?: boolean;
    buttonLoader?: boolean;
  },
  options?: MutationHookOptions<TData, TVariables>
) {
  const dispatch = useDispatch();
  const withLoader = config?.withLoader ?? true; // default true
  const buttonLoader = config?.buttonLoader ?? true; // default true
  const abortControllerRef = useRef<AbortController | null>(null);
  const location = useLocation();
  const context = useRef({
    fetchOptions: {
      signal: undefined as AbortSignal | undefined
    }
  }).current;

  // Set the signal if the request is active
  if (!context.fetchOptions.signal) {
    abortControllerRef.current = new AbortController();
    context.fetchOptions.signal = abortControllerRef.current.signal;
  }
  const [mutateFn, result] = useMutation<TData, TVariables>(mutation, {
    ...options,
    context,
    onCompleted: (data) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (buttonLoader) {
        dispatch(setButtonLoading('success'));
        setTimeout(() => dispatch(setButtonLoading('idle')), 500);
      }
      if (config?.successMsg) {
        toast.success(
          <ToastSuccessMessage>{config.successMsg}</ToastSuccessMessage>
        );
      }

      if (options?.onCompleted) options.onCompleted(data);
    },
    onError: (error) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (buttonLoader) {
        dispatch(setButtonLoading('idle'));
      }
      const cleanMessage = extractMessage(error.message);
      if (options?.onError) options.onError(error);
      dispatch(setError({ key, error: cleanMessage }));
      toast.error(cleanMessage);
    }
  });

  const wrappedMutate = async (variables?: TVariables) => {
    if (withLoader) {
      dispatch(setLoading({ key, value: true }));
    }
    if (buttonLoader) {
      dispatch(setButtonLoading('success'));
    }
    dispatch(setError({ key, error: null }));

    return mutateFn({
      variables,
      context: {
        fetchOptions: {
          signal: (abortControllerRef.current = new AbortController()).signal
        }
      }
    });
  };

  // cancel mutation when route changes or component unmounts
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (result.loading) {
      if (withLoader) {
        dispatch(setLoading({ key, value: true }));
      }
      if (buttonLoader) {
        dispatch(setButtonLoading('loading'));
      }
      dispatch(setError({ key, error: null }));
    }
  }, [result.loading]);

  return [wrappedMutate, result] as const;
}
